import { Handle, Position, useReactFlow } from "reactflow"
import "./SheetRenderer.css"
import { UICell, UINode, UISheet, UISheetType } from "../../types/UITypes"
import clsx from "clsx"
import { KeyboardEvent, MouseEvent, useContext, useMemo, useState } from "react"
import { AppContext } from "../../AppContext"
import { FormulaEditor } from "../FormulaEditor"
import CellLabelRenderer from "./CellLabelRenderer"
import { CELL_WIDTH, ROW_INDEX_WIDTH } from "../../utils/constants"
import CellContextMenu from "./CellContextMenu"

type CellRendererProps = {
  cell: UICell
  rowSize: number
  sheet: UISheet
  connectionModeEnabled: boolean
  resizeState: {
    isActive: boolean
    width: number
    height: number
    initialWidth: number
  }
  isSheetSelected: boolean
  onAddColumn: (sheetId: string, columnIndex: number) => void
  onCellMouseUp: (event: MouseEvent, rowIndex: number) => void
  onEditCell: (formulaEditorInlineValue: string, cell: UICell) => Promise<void>
  getNodeTopPos: (
    cell: UICell,
    i: number,
    nodeType: string,
    edgeHandles: UINode[]
  ) => string
  isComplexSheetLeftColumnCell?: boolean
  showConnectionHandles?: boolean
  showColumnContextMenu?: boolean
  showRowContextMenu?: boolean
  sheetColumnsCount?: number
  sheetRowsCount?: number
  draggedColumnIndex?: number
  hoveredColumnIndex?: number
  draggedRowIndex?: number
  hoveredRowIndex?: number
  sheetRows?: UICell[][]
  width?: number
  onMultiselectHandleMouseDown?: (event: MouseEvent, cell: UICell) => void
  onCellMouseOver?: (event: MouseEvent, cell: UICell) => void
}

export const CellRenderer = ({
  sheet,
  cell,
  onCellMouseUp,
  isSheetSelected,
  rowSize,
  onEditCell,
  getNodeTopPos,
  connectionModeEnabled,
  resizeState,
  showConnectionHandles = true,
  showColumnContextMenu = false,
  showRowContextMenu = false,
  sheetColumnsCount = 1,
  sheetRowsCount = 1,
  draggedColumnIndex,
  hoveredColumnIndex,
  draggedRowIndex,
  hoveredRowIndex,
  sheetRows,
  width,
  onMultiselectHandleMouseDown,
  onCellMouseOver,
}: CellRendererProps) => {
  const {
    sheetFields,
    isSheetsUIVisible,
    highlightedPathCellIds,
    setHighlightedPathDestCellId,
    onCellClick,
    selectedCell,
    setSelectedCell,
    setSelectedNodes,
    editedCell,
    setEditedCell,
    cellWithContextMenu,
    setCellWithContextMenu,
    validationErrors,
    isDraggingNode,
    onCellMouseDown,
    multiselectedCells,
    setHoveredCell,
  } = useContext(AppContext)

  const [internalValue, setInternalValue] = useState(cell.name)

  const onCellKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    cell: UICell
  ) => {
    let nextRowIndex = cell.rowIndex
    let nextColumnIndex = cell.columnIndex

    if (
      event.nativeEvent.key === "Shift" ||
      event.nativeEvent.key === "Control"
    ) {
      return
    }

    switch (event.nativeEvent.key) {
      case "ArrowRight": {
        if (editedCell?.id === cell.id) {
          return
        }

        nextColumnIndex =
          nextColumnIndex < sheetColumnsCount
            ? nextColumnIndex + 1
            : nextColumnIndex
        break
      }
      case "ArrowLeft": {
        if (editedCell?.id === cell.id) {
          return
        }

        nextColumnIndex =
          nextColumnIndex > 1 ? nextColumnIndex - 1 : nextColumnIndex
        break
      }
      case "ArrowUp": {
        nextRowIndex = nextRowIndex > 1 ? nextRowIndex - 1 : nextRowIndex
        break
      }
      case "ArrowDown": {
        nextRowIndex =
          nextRowIndex < sheetRowsCount ? nextRowIndex + 1 : nextRowIndex
        break
      }
      case "Enter": {
        event.preventDefault()
        setEditedCell(cell)
        return
      }
    }

    const nextCell =
      sheetRows && sheetRows[nextRowIndex - 1][nextColumnIndex - 1]

    if (nextCell) {
      if (
        nextCell.id !== cell.id &&
        internalValue !== undefined &&
        internalValue !== cell.name
      ) {
        onEditCell(internalValue, cell)
      }

      setSelectedCell(nextCell)
      // onCellMouseDown(nextCell, sheetRows, false)
      setEditedCell(undefined)
    }
  }

  let calculatedWidth = width || CELL_WIDTH
  // let calculatedWidth = isComplexSheetLeftColumnCell
  //   ? SHORT_CELL_WIDTH
  //   : CELL_WIDTH

  if (resizeState?.isActive && cell.columnIndex === rowSize) {
    calculatedWidth =
      resizeState?.width - ROW_INDEX_WIDTH - CELL_WIDTH * (rowSize - 1)
  }

  const sheetErrors = validationErrors[cell.sheetId]
  const validationError = sheetErrors && sheetErrors[cell.id]

  // if (resizeState?.isActive && cell.columnIndex === rowSize) {
  //   calculatedWidth =
  //     resizeState?.width -
  //     ROW_INDEX_WIDTH -
  //     CELL_WIDTH * (rowSize - 2) -
  //     (isComplexSheetLeftColumnCell ? SHORT_CELL_WIDTH : CELL_WIDTH)
  // }
  const isMultiselected = useMemo(() => {
    return (
      multiselectedCells?.sheetId === sheet.id &&
      multiselectedCells?.startColumn <= cell.columnIndex &&
      multiselectedCells?.endColumn >= cell.columnIndex &&
      multiselectedCells?.startRow <= cell.rowIndex &&
      multiselectedCells?.endRow >= cell.rowIndex
    )
  }, [multiselectedCells, cell.id])

  return (
    <div
      tabIndex={0}
      onFocus={(event) => {
        if (!isMultiselected) {
          setSelectedCell(cell)
          setEditedCell(cell)
          setSelectedNodes([`sheet-${cell.sheetId}`])
        }
      }}
      onMouseLeave={() => {
        setHoveredCell(undefined)
      }}
      onMouseEnter={() => {
        setHoveredCell(cell.id)
      }}
      key={cell.id}
      id={`cell-${cell.id}`}
      className={clsx("cell", {
        "edited-cell": editedCell?.id === cell.id,
        "resize-active": resizeState?.isActive,
        "highlighted-cell":
          (!isDraggingNode &&
            !validationError &&
            editedCell?.id !== cell.id &&
            highlightedPathCellIds.includes(cell.id)) ||
          (!isDraggingNode &&
            !validationError &&
            draggedColumnIndex !== undefined &&
            draggedColumnIndex + 1 === cell.columnIndex) ||
          (!isDraggingNode &&
            !validationError &&
            draggedRowIndex !== undefined &&
            draggedRowIndex === cell.rowIndex) ||
          isMultiselected,
        "selected-cell": selectedCell?.id === cell.id,
        "hovered-cell":
          (!isDraggingNode &&
            !validationError &&
            hoveredColumnIndex !== undefined &&
            hoveredColumnIndex !== draggedColumnIndex &&
            hoveredColumnIndex + 1 === cell.columnIndex) ||
          (!isDraggingNode &&
            !validationError &&
            hoveredRowIndex !== undefined &&
            hoveredRowIndex !== draggedRowIndex &&
            hoveredRowIndex === cell.rowIndex),
        "cell-with-validation-error": validationError,
        "is-dragging-node": isDraggingNode,
      })}
      onMouseOver={(event) => {
        setHighlightedPathDestCellId(cell.id)
      }}
      onMouseMove={(event) => {
        onCellMouseOver && onCellMouseOver(event, cell)
      }}
      onMouseOut={() => setHighlightedPathDestCellId(undefined)}
      onMouseUp={(event: MouseEvent) => onCellMouseUp(event, cell.rowIndex)}
      onMouseDown={(event: MouseEvent) => {
        if (sheetRows && !isMultiselected) {
          onCellMouseDown(cell, sheetRows, event.shiftKey)
        }
      }}
      onKeyDown={
        sheet.entityType === UISheetType.MATRIX || UISheetType.SHEET
          ? (event: KeyboardEvent<HTMLDivElement>) => onCellKeyDown(event, cell)
          : undefined
      }
      style={{
        width: calculatedWidth,
        maxWidth: calculatedWidth,
        outline: "none",
        ...(isMultiselected
          ? {
              borderLeft:
                multiselectedCells?.startColumn === cell.columnIndex
                  ? "1px dashed gray"
                  : "",
              borderRight:
                multiselectedCells?.endColumn === cell.columnIndex
                  ? "1px dashed gray"
                  : "",
              borderTop:
                multiselectedCells?.startRow === cell.rowIndex
                  ? "1px dashed gray"
                  : "",
              borderBottom:
                multiselectedCells?.endRow === cell.rowIndex
                  ? "1px dashed gray"
                  : "",
            }
          : {}),
      }}
      onClick={() => onCellClick(cell)}
      onContextMenu={(event) => {
        event.preventDefault()
        event.stopPropagation()

        setCellWithContextMenu({ cellId: cell.id, target: "cell" })
      }}
    >
      <div className="validation-error-tooltip">{validationError}</div>
      {/* {selectedCell?.id === cell.id && editedCell?.id === cell.id ? ( */}
      {selectedCell?.id === cell.id && !isMultiselected ? (
        <FormulaEditor
          cell={cell}
          onConfirm={onEditCell}
          onInternalValueChange={setInternalValue}
          sheetFields={sheetFields}
          editingEnabled={editedCell?.id === cell.id}
        />
      ) : (
        <CellLabelRenderer cell={cell} />
      )}
      {cell.columnIndex === rowSize && showConnectionHandles && (
        <>
          {sheet.edgeHandles
            ?.filter(
              (edgeHandle) =>
                edgeHandle.nodeType === "source" &&
                edgeHandle.rowIndex === cell.rowIndex
            )
            .map((edgeHandle, i) => (
              <Handle
                key={`edge-${edgeHandle.id}`}
                id={`edge-${edgeHandle.id}`}
                type="source"
                position={Position.Right}
                style={{
                  top: getNodeTopPos(
                    cell,
                    i,
                    edgeHandle.nodeType,
                    sheet.edgeHandles
                  ),
                  transform: "translateY(-50%)",
                  bottom: "auto",
                  background: "#4B91EC",
                  border: "1px solid white",
                }}
              />
            ))}
          {!resizeState.isActive && (
            <Handle
              type="source"
              position={Position.Right}
              id={`source-sheet-${cell.sheetId}-row-${cell.rowIndex}`}
              style={{
                visibility: connectionModeEnabled ? "visible" : "hidden",
              }}
              className="source-handle"
              isConnectable
            />
          )}
        </>
      )}
      {cell.columnIndex === 1 &&
        !isSheetSelected &&
        !isSheetsUIVisible &&
        showConnectionHandles && (
          <>
            <Handle
              type="target"
              position={Position.Left}
              id={`target-sheet-${cell.sheetId}-row-${cell.rowIndex}`}
              style={{
                visibility: connectionModeEnabled ? "visible" : "hidden",
              }}
              className="target-handle"
              isConnectable
              // onMouseOver={() => onCellHighlight(cell.id)}
              // onMouseOut={() => onCellHighlight(undefined)}
            />
            {sheet.edgeHandles
              ?.filter(
                (edgeHandle) =>
                  edgeHandle.nodeType === "target" &&
                  edgeHandle.rowIndex === cell.rowIndex
              )
              .map((edgeHandle, i) => (
                <Handle
                  key={`edge-${edgeHandle.id}`}
                  id={`edge-${edgeHandle.id}`}
                  type="target"
                  position={Position.Left}
                  style={{
                    top: getNodeTopPos(
                      cell,
                      i,
                      edgeHandle.nodeType,
                      sheet.edgeHandles
                    ),
                    transform: "translateY(-50%)",
                    bottom: "auto",
                    background: "none",
                    border: "none",
                  }}
                />
              ))}
          </>
        )}
      <CellContextMenu
        cell={cell}
        cellWithContextMenu={cellWithContextMenu}
        sheet={sheet}
        target="cell"
        showColumnContextMenu={showColumnContextMenu}
        showRowContextMenu={showRowContextMenu}
      />
      {selectedCell?.id === cell.id ? (
        <>
          <div
            style={{
              position: "absolute",
              top: "-2px",
              left: "-2px",
              width: "6px",
              height: "6px",
              backgroundColor: "#0966de",
              borderRadius: "50%",
              cursor: "pointer",
              zIndex: 100,
            }}
            onMouseDown={(event) => {
              event.stopPropagation()

              onMultiselectHandleMouseDown &&
                onMultiselectHandleMouseDown(event, cell)
            }}
          ></div>
          <div
            style={{
              position: "absolute",
              bottom: "-3px",
              right: "-3px",
              width: "6px",
              height: "6px",
              backgroundColor: "#0966de",
              borderRadius: "50%",
              cursor: "pointer",
              zIndex: 100,
            }}
            onMouseDown={(event) => {
              event.stopPropagation()

              onMultiselectHandleMouseDown &&
                onMultiselectHandleMouseDown(event, cell)
            }}
          ></div>
        </>
      ) : null}
    </div>
  )
}
export default CellRenderer
